<template>
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customers.sync="customerCollection"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="onQueryCompleted"
    @query-id-change="queryId = $event"
    ref="parentLayout"
  >
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term-select v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="category-item-category" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <filter-category-item-category
          :company="queryFilter.item.company"
          v-bind.sync="queryFilter.item"
          :use-new-item-component="true"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- 顧客グループ -->
      <query-filter-element id="customer" title="顧客グループ">
        <filter-customer v-bind.sync="queryFilter.customer" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option" title="オプション">
        <filter-option v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <div
        :gl-tooltip="
          !$store.state.isQueryFullFeatured
            ? msg.MSG_QUERY_FULL_FEATURE_UNAVAILABLE
            : ''
        "
        gl-tooltip-direction="left"
        class="button-group button-group__layout"
      >
        <md-button
          v-for="level in itemCategoryLevels"
          class="md-raised button-group__button"
          :value="level.level"
          :key="level.level"
          :disabled="!$store.state.isQueryFullFeatured"
          @click="drilldown(level.level)"
          >{{ level.name }}へ
        </md-button>
      </div>
      <gl-table ref="myTable" v-bind="resultTable" />
      <md-snackbar
        md-position="center"
        :md-duration="4000"
        :md-active.sync="showSnackbar"
        md-persistent
      >
        <span>対象の分類を一覧から選択してください</span>
      </md-snackbar>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermSelect from '@/components/FilterTermSelect.vue'
import FilterCategoryItemCategory from '@/components/FilterCategoryItemCategory.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterCustomer from '@/components/FilterCustomer.vue'
import FilterOption from '@/components/FilterOptionSingleEvaluation.vue'
// 分析条件モデル
import termSelect from '@/queryfilter/termSelect'
import categoryItemCategory from '@/queryfilter/categoryItemCategory2'
import store from '@/queryfilter/store'
import customer from '@/queryfilter/customer'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import { searchCellCustomFunction } from '@/util/custom_formatters'
import msg from '@/const/messages'
let hold = false

export default {
  components: {
    QueryFilterElement,
    FilterTermSelect,
    FilterCategoryItemCategory,
    FilterStore,
    FilterCustomer,
    FilterOption,
    LongQueryLayout,
    GlTable,
  },
  props: {
    /**
     * 自画面のドリルダウンや他画面からの遷移時に渡される引き継ぎID
     *
     * あくまで画面間で分析条件を引き継ぐための識別子で、分析時にサーバーで
     * 発行されるクエリIDとは別なので注意してください。
     *
     * メニューから最初に開く場合など、入力がない場合もあります。
     */
    inherit: {
      type: String,
    },
  },
  data() {
    return {
      msg,
      resourceName: 'tempura-i',
      bottomItemCategoryLevel: undefined,
      showSnackbar: false,
      /**
       * ドリルダウン先の階層
       */
      itemCategoryLevels: [],

      queryId: undefined,
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, summaryKey: 'termSelect', ...termSelect.get() },
        item: {
          order: 2,
          summaryKey: 'categoryItemCategory',
          ...categoryItemCategory.get(),
        },
        store: {
          order: 3,
          //  選択可能店舗数制限
          storeCountMax: process.env.VUE_APP_STORE_COUNT_MAX
            ? Number(process.env.VUE_APP_STORE_COUNT_MAX)
            : 200,
          ...store.get(),
        },
        customer: { order: 4, ...customer.get() },
        option: {
          order: 5,
          evaluation: 'amount',
          summaryKey: 'optionSingleEvaluation',
          errors: [],
          visibleCustomerSegments: [],
        },
        drilldown: {
          queryId: undefined,
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'code',
        sortOrder: 'asc',
        selectable: true,
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          linkify: (rowData, cell, value, colName) => {
            cell.classList.add('customer-collection-cell')
            cell.setAttribute(
              'data-customer-collection',
              rowData.customer_filter[colName],
            )

            const toggle = e => {
              if (!this.resultTable.columns) {
                throw new Error('Column not found')
              }
              // hit the column
              const col = this.resultTable.columns.find(
                c => c.colName === colName,
              )

              const id = e.target.getAttribute('data-customer-collection')
              let text

              if (colName === 'customer_count') {
                text = rowData.name
              } else {
                text = `${rowData.name} - ${col.headerText}`
              }

              const customer = { id, text }

              this.$emit('toggle-customer-collection', customer)
            }

            cell.addEventListener('mousedown', e => {
              hold = true
              document.body.classList.add('no-select')
              toggle(e)
            })
            cell.addEventListener('mouseup', () => {
              hold = false
              document.body.classList.remove('no-select')
            })

            cell.addEventListener('mouseenter', e => {
              // console.log('hold', hold)
              if (!hold) {
                return
              }
              toggle(e)
            })
          },
          linkifySearch: searchCellCustomFunction,
        },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: termSelect.mapToApiPayload(queryFilter.term),
        category:
          queryFilter.item.selectMode === 'category'
            ? categoryItemCategory.mapToApiPayload(queryFilter.item)
            : null,
        item:
          queryFilter.item.selectMode === 'category'
            ? null
            : categoryItemCategory.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        customer: customer.mapToApiPayload(queryFilter.customer),
        option: {
          evaluation: queryFilter.option.evaluation,
        },
        drilldown: queryFilter.drilldown,
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        termSelect.applyPayload(this.queryFilter.term, queryFilter.term)
        categoryItemCategory.applyPayload(
          this.queryFilter.item,
          queryFilter.item,
        )
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        customer.applyPayload(this.queryFilter.customer, queryFilter.customer)
        this.queryFilter.option.evaluation = queryFilter.option.evaluation
        if (queryFilter.drilldown) {
          this.queryFilter.drilldown.queryId = undefined
        }
      },
      /**
       * 顧客コレクション
       */
      customerCollection: [],
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
        }
      },
    }
  },
  watch: {
    'queryFilter.item.selectMode'(newVal) {
      this.resultTable.selectable = newVal === 'category'
    },
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height - 50
    },
    /**
     * クエリ完了
     */
    onQueryCompleted() {
      this.loadItemCategoryLevelsData()
      this.$refs.myTable.init()
    },
    /**
     * 選択しているカテゴリ階層以下＋商品をロード
     */
    loadItemCategoryLevelsData() {
      if (this.queryFilter.item.selectMode === 'category') {
        const itemCategoryLevels = this.$store.state.itemCategoryLevels[
          this.queryFilter.item.company
        ]

        //商品のために最下層を取得しておく
        this.bottomItemCategoryLevel =
          itemCategoryLevels[itemCategoryLevels.length - 1].level

        //条件で選択している階層までは削除
        const deleteIndex = itemCategoryLevels.findIndex(
          i => i.level == this.queryFilter.item.category.itemCategoryLevel,
        )

        this.itemCategoryLevels = [
          ...itemCategoryLevels.slice(deleteIndex + 1),
          {
            level: null,
            name: '商品',
          },
        ]
      } else {
        this.itemCategoryLevels = []
      }
    },
    /**
     * ドリルダウン処理
     **/
    async drilldown(itemCategoryLevel) {
      const selectedRows = this.$refs.myTable.getSelectedRowData()
      const allChecked = this.$refs.myTable.isSelectAllChecked()

      if (selectedRows.length === 0 && !allChecked) {
        this.showSnackbar = true
        return
      }
      this.showSnackbar = false
      this.queryFilter.drilldown.queryId = this.queryId
      const fromItemCategoryLevel = this.queryFilter.item.category
        .itemCategoryLevel
      const toItemCategoryLevel =
        itemCategoryLevel !== null
          ? itemCategoryLevel
          : this.bottomItemCategoryLevel
      let categories = selectedRows.map(r => r.code)

      // 全選択チェックボックス入っている場合はselectedRows関係なく全行取得してドリルダウンを行う。
      if (allChecked) {
        // 全データのカテゴリコード取得
        const { name, order } = this.$refs.myTable.getCurrentSort()
        // 取得行数を99999にして、全件取得する。
        const params = {
          numberOfRows: 99999,
          currentPage: 1,
          sortColumnName: name,
          sortOrder: order,
        }

        categories = await this.$longQuery.fetchAllCategoryListForDrillDown(
          this.resourceName,
          this.queryId,
          params,
        )
      }
      const drilldownCategories = await this.getTargetDrilldownCategories(
        this.queryFilter.item.company,
        fromItemCategoryLevel,
        toItemCategoryLevel,
        categories,
      )

      if (itemCategoryLevel == null) {
        //商品へは商品(分類)へ切り替え
        this.queryFilter.item.selectMode = 'itemCategory'
        this.queryFilter.item.item.categories = drilldownCategories
      } else {
        //各カテゴリへ
        this.queryFilter.item.category.itemCategoryLevel = itemCategoryLevel
        this.queryFilter.item.category.categories = drilldownCategories
      }

      this.$refs.parentLayout.executeQuery()
    },
    /**
     * fromのcategoriesよりtoのカテゴリを取得
     */
    async getTargetDrilldownCategories(company, from, to, categories) {
      const chunkSize = 100
      const targetDrilldownCategories = []
      for (let i = 0; i < categories.length; i += chunkSize) {
        const chunkCategories = categories.slice(i, i + chunkSize)
        const chunkDrilldownCategories = await this.$repositories
          .get('itemCategory')
          .getDrilldownData(company, from, to, chunkCategories)
        targetDrilldownCategories.push(...chunkDrilldownCategories)
      }
      return targetDrilldownCategories.map(c => c.code)
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.item.itemGroupId = undefined
      return queryFilter
    },
  },
}
</script>
